import { createTheme, alpha, lighten, darken } from '@mui/material/styles';

/**
 * Brand-Farben, die in beiden Modi identisch bleiben.
 * primary wird pro Modus separat definiert (siehe unten), weil #3a20cd auf
 * dunklem Grund kaum 4.5:1 Kontrast erreicht.
 */
const brand = {
  // mirrors primary: the app has no second action level, secondary only marks accents (badges, dots, bars)
  secondary: {
    light: '#6a50e0',
    main: '#3a20cd',
    dark: '#2a1790',
    contrastText: '#fff',
  },
  tertiary: {
    light: '#b556d9',
    main: '#9f2ccc',
    dark: '#6f1e8f',
    contrastText: '#fff',
  },
  error: {
    light: '#e57373',
    main: '#f44336',
    dark: '#d32f2f',
  },
  custom: {
    light: '#ffab00',
    main: '#ffab00',
    dark: '#ffab00',
    contrastText: '#fff',
  },
};

// Primary's hue with less saturation: links, accents and the dashboard chart
const accentMuted = { light: '#5946c3', dark: '#7e6fce' };

// Text on the accent surfaces in dark mode
const darkAccentText = '#c2baef';

// Dark amber: warning text in light mode, the warning surfaces in dark mode
const amberDark = '#9f6000';

/**
 * Light-Palette — visuell identisch zum bisherigen statischen Theme.
 * background/text/divider werden bewusst NICHT gesetzt, damit MUIs
 * Light-Defaults exakt wie zuvor greifen. Neu sind nur die semantischen
 * Tokens (infoBar/warningBar/fieldRowAlt/tileGradient), die der
 * Komponenten-Sweep (Ticket 6) als Ziel nutzt.
 */
const lightPalette = {
  mode: 'light',
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
  },
  primary: {
    light: '#6a50e0',
    main: '#3a20cd',
    dark: '#2a1790',
    contrastText: '#fff',
  },
  ...brand,
  /**
   * info/success/warning waren bisher nicht definiert, MUI hat sie mit seinen
   * eigenen — modusabhängigen — Defaults gefüllt. Hier stehen exakt diese
   * Light-Defaults, damit der Light-Modus unverändert bleibt und die Werte,
   * auf denen die Alert-Flächen rechnen, im Repo nachvollziehbar sind.
   */
  info: {
    light: '#03a9f4',
    main: '#0288d1',
    dark: '#01579b',
    contrastText: '#fff',
  },
  success: {
    light: '#4caf50',
    main: '#2e7d32',
    dark: '#1b5e20',
    contrastText: '#fff',
  },
  warning: {
    light: '#ff9800',
    main: '#ed6c02',
    dark: '#e65100',
    contrastText: '#fff',
  },
  /**
   * Linkfarbe. Es gibt app-weit keine globale `a`-Regel, ungestylte Anchor
   * nahmen deshalb die Browser-Defaults — #0000EE (1.77:1) bzw. besucht
   * #551A8B (1.51:1) auf dunklem Grund. Auch MUIs `<Link>`-Default (primary)
   * reisst dort mit 3.05:1 die AA-Schwelle.
   *
   * Light: accentMuted, like --ly-link in index.css.
   * Dark nutzt einen aufgehellten Indigo mit 6.87:1 — bewusst mehr Reserve als
   * primary.light (4.65:1), damit auch kleine Schrift und getönte Flächen
   * tragen. `visited` läuft absichtlich auf derselben Farbe.
   */
  link: accentMuted.light,
  // Semantische Tokens (Light)
  infoBar: {
    background: '#f1f8e9',
    text: '#33691e',
  },
  warningBar: {
    background: '#feefb3',
    text: amberDark,
  },
  errorBar: {
    background: '#ffebee',
    text: '#f44336',
  },
  fieldRowAlt: 'rgba(0, 0, 0, 0.025)',
  // Zurückgesetzte Fläche: aufgeklappter Listeneintrag, deaktivierte Felder,
  // Feld-Labels und Abschnittsköpfe der Übersetzungsfelder.
  surfaceRecessed: '#fafafa',
  // Accent surface: toolbars of the flex lists, placeholders for new fields
  accentSurface: {
    background: '#eeecf9',
    text: accentMuted.light,
  },
  // Unsaved-changes bar, opaque because it is fixed over scrolling content
  saveBar: {
    background: lighten(brand.secondary.light, 0.9),
    text: brand.secondary.main,
  },
  skeletonBlock: '#eee',
  tileGradient: 'linear-gradient(135deg, #3a20cd 0%, #9f2ccc 100%)',
  accentMuted: accentMuted.light,
};

/**
 * Dark-Palette. primary wird eine Stufe aufgehellt (das bisherige
 * primary.light #6a50e0 wird zum main), damit Buttons/Links auf dunklem
 * Grund genug Kontrast haben. background/text/divider überlassen wir
 * weitgehend MUIs Dark-Defaults und setzen nur die Flächen explizit.
 */
const darkPalette = {
  mode: 'dark',
  primary: {
    light: '#8b74f0',
    main: '#6a50e0',
    dark: '#3a20cd',
    contrastText: '#fff',
  },
  ...brand,
  // mirrors dark primary, like brand.secondary does in light mode
  secondary: {
    light: '#8b74f0',
    main: '#6a50e0',
    dark: '#3a20cd',
    contrastText: '#fff',
  },
  // mirrors the light mode's hierarchy: the page ground (body, sidebar) is the grey, the content
  // surfaces (items, dialogs, app bar) are the darker tone, so the text the merchant reads has the
  // higher contrast - Material's "higher is lighter" would put the contrast on the navigation
  background: {
    default: '#1e1e1e',
    paper: '#121212',
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.92)',
    secondary: 'rgba(255, 255, 255, 0.7)',
  },
  divider: 'rgba(255, 255, 255, 0.12)',
  /**
   * Status-Paletten für den Dark Mode, eine Stufe aufgehellt (MUIs
   * Dark-Defaults, jetzt explizit). Auf .main rechnen die Alert-Flächen in
   * `components.MuiAlert`, .light liefert die Text- und Icon-Farbe darauf.
   *
   * `palette.action` bleibt bewusst bei MUIs Defaults: `action.active` ist im
   * Dark Mode `#fff`, und das ist auf dunklem Grund richtig. Wer Icons auf
   * einer hell gehaltenen Insel platziert, muss die Fläche mitziehen statt
   * diese Palette umzubiegen — sonst verschwinden App-weit alle IconButtons.
   */
  info: {
    light: '#4fc3f7',
    main: '#29b6f6',
    dark: '#0288d1',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  success: {
    light: '#81c784',
    main: '#66bb6a',
    dark: '#388e3c',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  warning: {
    light: '#ffb74d',
    main: '#ffa726',
    dark: '#f57c00',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  // Linkfarbe, 6.87:1 auf background.paper — siehe Kommentar in lightPalette
  link: '#a99af7',
  // Semantische Tokens (Dark) — abgedunkelte Varianten der Status-Flächen
  infoBar: {
    background: 'rgba(51, 105, 30, 0.25)',
    text: '#aed581',
  },
  warningBar: {
    background: alpha(amberDark, 0.3),
    text: '#ffd54f',
  },
  errorBar: {
    background: 'rgba(244, 67, 54, 0.18)',
    // the red-tinted #ef9a9a read poorly on the tint; text.primary, the icons keep error.light
    text: 'rgba(255, 255, 255, 0.92)',
  },
  fieldRowAlt: 'rgba(255, 255, 255, 0.04)',
  // Zurückgesetzte Fläche: aufgeklappter Listeneintrag, deaktivierte Felder,
  // Feld-Labels und Abschnittsköpfe der Übersetzungsfelder.
  surfaceRecessed: '#181818',
  // Accent surface: toolbars of the flex lists, placeholders for new fields
  accentSurface: {
    background: alpha(accentMuted.dark, 0.18),
    text: darkAccentText,
  },
  // Unsaved-changes bar, opaque because it is fixed over scrolling content
  saveBar: {
    background: '#211c33',
    text: darkAccentText,
  },
  skeletonBlock: 'rgba(255, 255, 255, 0.11)',
  tileGradient: 'linear-gradient(135deg, #2a1790 0%, #6f1e8f 100%)',
  accentMuted: accentMuted.dark,
};

/**
 * Fläche für Alerts der Variante `standard` im Dark Mode.
 *
 * MUI berechnet sie als `darken(<severity>.light, 0.9)`. `darken` skaliert alle
 * drei Kanäle: das Hue-Verhältnis bleibt, die absolute Chroma kollabiert. Jede
 * Severity landet dadurch bei 1.01–1.03:1 zur Seitenfläche (#121212) — der Text
 * bleibt lesbar, aber die Leiste ist als Leiste nicht mehr erkennbar.
 *
 * Stattdessen `alpha(<severity>.main, opacity)`. `error` braucht einen höheren
 * Wert, weil #f44336 dunkler aufbaut als die anderen drei und bei 0.25 mit
 * 1.34:1 das schwächste Glied bliebe.
 *
 * Die Textfarbe ist `<severity>.light`, was 5.9–6.5:1 auf der neuen Fläche
 * ergibt. Nur bei `error` reicht das nicht: #e57373 auf rgb(90,34,30) sind
 * 4.18:1 und damit unter den 4.5:1, die AA für normalen Text fordert — daher
 * dort das hellere `errorBar.text` (5.80:1), das ohnehin schon die Rolle
 * „Text auf einer error-Fläche" besetzt.
 *
 * Light-Modus gibt `{}` zurück — MUIs Werte sind dort korrekt.
 */
// Surface and text of a standard alert. Exported for banners that are not an Alert but
// should look like one (the copy-paste code rows in the static section, the snackbars).
// Dark mode options: `opacity` of the tint, `tint`/`text`/`icon` as palette accessors.
export const alertSurface = (theme, severity, dark = {}) => {
  if (theme.palette.mode !== 'dark') {
    // MUI's own light values, spelled out so the copies match
    return {
      ...frostedSurface(lighten(theme.palette[severity].light, 0.9)),
      color: darken(theme.palette[severity].light, 0.6),
    };
  }

  const pick = (accessor, fallback) => (accessor ? accessor(theme.palette) : fallback);
  const tint = pick(dark.tint, theme.palette[severity].main);
  const icon = pick(dark.icon, theme.palette[severity].light);

  // text.primary, not the severity tone: same-hue text on a same-hue tint reads poorly even
  // at 5:1, the icon carries the severity colour
  return {
    ...frostedSurface(alpha(tint, dark.opacity ?? 0.25)),
    color: pick(dark.text, theme.palette.text.primary),
    '& .MuiAlert-icon': {
      color: icon,
    },
    // the lightened primary of outlined/text buttons has no contrast on the tints
    '& .MuiButton-outlined, & .MuiButton-text': {
      color: 'inherit',
      borderColor: 'currentColor',
    },
  };
};

const darkAlertSurface = (severity, dark) => ({ theme }) => alertSurface(theme, severity, dark);

/**
 * Text color of primary buttons and icons in dark mode. primary.main (#6a50e0)
 * is too dark on the dark surfaces; the lightened link color carries much
 * better. Surfaces stay untouched: contained buttons keep primary.main as
 * background.
 */
const darkPrimaryText = ({ theme }) => (
  theme.palette.mode === 'dark' ? { color: theme.palette.link } : {}
);

// Banner surface: the tint as given, with the content behind it blurred. The tilted band
// behind the page used to cut a sharp edge through the translucent dark tints.
const frostedSurface = (background) => ({
  backgroundColor: background,
  WebkitBackdropFilter: 'blur(12px)',
  backdropFilter: 'blur(12px)',
});

// Banner on one of the bar tokens (infoBar, warningBar, errorBar, accentSurface): the tint
// with blur, 16px like the alerts, white text in dark mode because the token's own text tone
// is the same hue as its tint. Links and outlined buttons follow the text; the tripled `&`
// outranks the page-wide link colour in index.css.
export const bannerSurface = (theme, token) => ({
  ...frostedSurface(token.background),
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : token.text,
  fontSize: 16,
  '&&& a': {
    color: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline',
  },
  '& .MuiButton-outlined, & .MuiButton-text': {
    color: 'inherit',
    borderColor: 'currentColor',
  },
});

const components = {
  // dark mode: paper is darker than the ground, an outline keeps the page sheets visible.
  // Only inside the main frame: menus, dialogs and snackbars render into portals and keep
  // their shadow; flat (elevation 0) papers stay borderless
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => (
        theme.palette.mode === 'dark'
          ? { 'main &.MuiPaper-elevation:not(.MuiPaper-elevation0)': { border: `1px solid ${theme.palette.divider}` } }
          : {}
      ),
    },
  },
  MuiButton: {
    styleOverrides: {
      textPrimary: darkPrimaryText,
      outlinedPrimary: darkPrimaryText,
    },
  },
  MuiIconButton: {
    styleOverrides: {
      colorPrimary: darkPrimaryText,
    },
  },
  MuiTab: {
    styleOverrides: {
      textColorPrimary: ({ theme }) => (
        theme.palette.mode === 'dark' ? { '&.Mui-selected': { color: theme.palette.link } } : {}
      ),
    },
  },
  MuiLink: {
    styleOverrides: {
      // no :visited rule: it outranked color="inherit" (links on info snackbars and banners turned
      // the link colour once visited); an author colour beats the browser's visited purple anyway.
      // Only the default colour is replaced: a root override beats the color prop's variant, so an
      // unconditional rule painted color="error" links in the link colour too
      root: ({ theme, ownerState }) => (ownerState.color === 'primary' ? {
        color: theme.palette.link,
      } : {}),
    },
  },
  MuiAlert: {
    styleOverrides: {
      /**
       * Links in Alerts nehmen die Textfarbe des Alerts, nicht die globale
       * Linkfarbe: auf einer getönten Statusfläche ist die Alert-Farbe der
       * richtige Bezug. Unterstrichen bleiben sie, sind also weiter als Link
       * erkennbar.
       *
       * Gilt in beiden Modi — im Light-Modus verbessert das den Kontrast
       * ebenfalls (Browser-Blau 8.47:1 → Alert-Textfarbe 9.58:1).
       */
      root: {
        '& a': {
          color: 'inherit',
          fontWeight: 600,
          textDecoration: 'underline',
        },
      },
      // body2 is 14px and read too small on the banners; nested body2 follows
      message: {
        fontSize: 16,
        lineHeight: 1.5,
        '& .MuiTypography-body2': {
          fontSize: 'inherit',
          lineHeight: 'inherit',
        },
      },
      // info alerts sit on the accent surface, not on MUI's blue: that is the tone the
      // notice boxes had before they became alerts
      standardInfo: ({ theme }) => theme.palette.mode === 'dark'
        ? alertSurface(theme, 'info', { tint: (p) => p.secondary.main, icon: (p) => p.accentSurface.text })
        : {
            ...frostedSurface(theme.palette.accentSurface.background),
            color: theme.palette.accentSurface.text,
            '& .MuiAlert-icon': { color: theme.palette.accentSurface.text },
          },
      standardSuccess: darkAlertSurface('success'),
      // warning.main is orange and read brown-red next to the error alert; the light amber
      // was too pale under white text, so the tint is the dark amber at high alpha (7.4:1)
      standardWarning: darkAlertSurface('warning', { opacity: 0.7, tint: () => amberDark, icon: (p) => p.warningBar.text }),
      standardError: darkAlertSurface('error', { opacity: 0.32 }),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: '8px 12px',
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        padding: '8px 12px',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: '14px 20px',
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        padding: '14px 0',
        height: 'initial',
      },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      // Modusabhängig: heller Schleier im Light-, dunkler im Dark-Modus.
      root: ({ theme }) => ({
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.5)'
            : 'rgba(255, 255, 255, .5)',
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: {
        zIndex: '13000 !important',
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      root: {
        zIndex: '13000 !important',
      },
    },
  },
  // tooltips default to 1500 and would sit behind the dialogs and popovers lifted above
  MuiTooltip: {
    styleOverrides: {
      popper: {
        zIndex: '13001 !important',
      },
      // body size and room to breathe for every hover: MUI's caption-sized default reads badly as
      // soon as a tooltip carries a sentence, and a label in the same box costs nothing
      tooltip: {
        fontSize: 13,
        lineHeight: 1.5,
        padding: '10px 12px',
        maxWidth: 360,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'standard',
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: 'standard',
    },
    styleOverrides: {
      // the standard input pads only the arrow side, so the underline ran flush against the text
      // on the left and past it on the right; the same inset on the left balances it.
      // The input's 14px bottom padding pushed the underline away from the text; the difference
      // moves to the root's margin so the select keeps its height next to text fields
      standard: {
        paddingLeft: 8,
        paddingBottom: 6,
      },
      root: {
        '&.MuiInput-root': {
          marginBottom: 9,
        },
      },
      // the arrow centres on the now shorter root; half the removed padding brings it back level
      // with the text. Standard only: outlined selects keep their padding and their arrow
      iconStandard: {
        top: 'calc(50% - .5em + 4px)',
      },
    },
  },
  MuiFormControl: {
    defaultProps: {
      variant: 'standard',
    },
  },
  // the rows-per-page select is a bare InputBase: it gets the 14px top padding above but not the
  // Input root's bottom margin, so the number and arrow sat low next to "Rows per page"
  MuiTablePagination: {
    styleOverrides: {
      select: {
        paddingTop: 6,
        paddingBottom: 6,
      },
      selectIcon: {
        top: 'calc(50% - .5em)',
      },
    },
  },
};

/**
 * Theme-Factory. Nimmt den aufgelösten Modus ('light' | 'dark') entgegen und
 * baut das passende MUI-Theme.
 * @param {'light'|'dark'} mode
 */
export function createAppTheme(mode = 'light') {
  return createTheme({
    palette: mode === 'dark' ? darkPalette : lightPalette,
    components,
    mixins: { frostedSurface },
  });
}

// Default-Export (Light) für Bestandscode, der `import theme from './theme'` nutzt.
const theme = createAppTheme('light');

export default theme;
